import { useState } from 'react';

interface StudentWork {
  id: number;
  title: string;
  author: string;
  grade: string;
  category: string;
  award: string;
  excerpt: string;
  image: string;
}

export default function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  
  // 管理員密碼 (實際使用時建議使用更安全的方式)
  const ADMIN_PASSWORD = 'write-123';

  const [works, setWorks] = useState<StudentWork[]>([
    {
      id: 1,
      title: "我的夢想",
      author: "小明",
      grade: "小三",
      category: "elementary",
      award: "校內作文比賽第一名",
      excerpt: "我的夢想是成為一名太空人，飛到月球上去看看嫦娥姊姊...",
      image: "https://public.readdy.ai/ai/img_res/0d2cc8642be1c15bf68ce8ae91ed82a8.jpg"
    },
    {
      id: 2,
      title: "春天的故事",
      author: "小華",
      grade: "小五",
      category: "elementary", 
      award: "全國作文競賽優選",
      excerpt: "春天來了，櫻花盛開，就像粉紅色的雲朵飄在樹梢上...",
      image: "https://readdy.ai/api/search-image?query=Beautiful%20Chinese%20essay%20about%20spring%20written%20by%20elementary%20student%2C%20showing%20elegant%20Chinese%20handwriting%20with%20poetic%20descriptions%20of%20cherry%20blossoms%20and%20nature.%20The%20composition%20displays%20traditional%20Chinese%20writing%20format%20with%20vertical%20text%2C%20decorative%20borders%2C%20and%20artistic%20elements.%20The%20layout%20shows%20academic%20achievement%20in%20Chinese%20literature%20with%20pink%20and%20white%20theme.&width=400&height=300&seq=chinese-work2&orientation=landscape"
    },
    {
      id: 3,
      title: "科技與人文",
      author: "小雯",
      grade: "國二",
      category: "junior",
      award: "市級議論文比賽第二名",
      excerpt: "在科技快速發展的時代，我們更應該重視人文精神的培養...",
      image: "https://readdy.ai/api/search-image?query=Sophisticated%20Chinese%20argumentative%20essay%20by%20junior%20high%20school%20student%20about%20technology%20and%20humanities%2C%20featuring%20well-structured%20Chinese%20writing%20with%20clear%20paragraphs%2C%20thesis%20statements%2C%20and%20supporting%20arguments.%20The%20composition%20shows%20advanced%20Chinese%20writing%20skills%20with%20proper%20formatting%2C%20academic%20vocabulary%2C%20and%20professional%20presentation%20in%20educational%20setting.&width=400&height=300&seq=chinese-work3&orientation=landscape"
    },
    {
      id: 5,
      title: "家鄉的味道",
      author: "小美",
      grade: "小四",
      category: "elementary",
      award: "校刊優秀作品",
      excerpt: "奶奶做的紅燒肉，是我記憶中最溫暖的味道...",
      image: "https://readdy.ai/api/search-image?query=Heartwarming%20Chinese%20essay%20about%20hometown%20flavors%20and%20grandmothers%20cooking%20by%20elementary%20student%2C%20featuring%20descriptive%20Chinese%20writing%20about%20traditional%20food%20and%20family%20memories.%20The%20composition%20shows%20creative%20Chinese%20writing%20with%20vivid%20descriptions%2C%20emotional%20expression%2C%20and%20cultural%20elements%20in%20a%20school%20publication%20format.&width=400&height=300&seq=chinese-work5&orientation=landscape"
    },
    {
      id: 6,
      title: "環保小尖兵",
      author: "小強",
      grade: "國一",
      category: "junior",
      award: "環保作文競賽特優",
      excerpt: "地球是我們共同的家園，保護環境是每個人的責任...",
      image: "https://readdy.ai/api/search-image?query=Environmental%20protection%20Chinese%20essay%20by%20junior%20high%20student%20with%20persuasive%20writing%20about%20conservation%20and%20responsibility.%20The%20composition%20features%20well-organized%20Chinese%20writing%20with%20clear%20arguments%2C%20environmental%20themes%2C%20and%20academic%20formatting.%20The%20layout%20shows%20excellence%20in%20Chinese%20composition%20with%20proper%20structure%20and%20compelling%20content.&width=400&height=300&seq=chinese-work6&orientation=landscape"
    }
  ]);

  const [editingWork, setEditingWork] = useState<StudentWork | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddingNew, setIsAddingNew] = useState(false);

  const categories = [
    { value: 'elementary', label: '國小作品' },
    { value: 'junior', label: '國中作品' }
  ];

  const grades = [
    '小一', '小二', '小三', '小四', '小五', '小六',
    '國一', '國二', '國三'
  ];

  const handleLogin = () => {
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setPasswordError('');
    } else {
      setPasswordError('密碼錯誤，請重新輸入');
      setPassword('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  // 如果未驗證，顯示登入頁面
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-pink-500 mb-2" style={{fontFamily: '"Pacifico", serif'}}>
                共學趣線上國語文寫作教室
              </h1>
              <h2 className="text-xl font-semibold text-gray-900">作品管理系統</h2>
              <p className="text-gray-600 mt-2">請輸入管理員密碼</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  管理員密碼
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder="請輸入密碼"
                />
                {passwordError && (
                  <p className="text-red-500 text-sm mt-2">{passwordError}</p>
                )}
              </div>
              
              <button
                onClick={handleLogin}
                className="w-full bg-pink-500 text-white py-3 rounded-lg font-medium hover:bg-pink-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                登入管理系統
              </button>
              
              <div className="text-center">
                <a 
                  href="/results" 
                  className="text-pink-500 hover:text-pink-600 text-sm cursor-pointer"
                >
                  返回作品展示頁面
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleEdit = (work: StudentWork) => {
    setEditingWork({ ...work });
    setIsAddingNew(false);
    setIsModalOpen(true);
  };

  const handleAddNew = () => {
    const newWork: StudentWork = {
      id: Math.max(...works.map(w => w.id)) + 1,
      title: '',
      author: '',
      grade: '小一',
      category: 'elementary',
      award: '',
      excerpt: '',
      image: 'https://readdy.ai/api/search-image?query=Traditional%20Chinese%20calligraphy%20and%20writing%20practice%20showing%20beautiful%20handwritten%20Chinese%20characters%20and%20essays%20by%20elementary%20school%20students.%20The%20composition%20features%20neat%20Chinese%20handwriting%20on%20traditional%20writing%20paper%20with%20red%20correction%20marks%2C%20alongside%20brushes%2C%20ink%20stones%2C%20and%20Chinese%20writing%20materials.%20The%20scene%20shows%20academic%20excellence%20in%20Chinese%20language%20arts%20with%20pink%20and%20white%20educational%20theme.&width=400&height=300&seq=new-work&orientation=landscape'
    };
    setEditingWork(newWork);
    setIsAddingNew(true);
    setIsModalOpen(true);
  };

  const handleSave = () => {
    if (!editingWork) return;

    if (isAddingNew) {
      setWorks([...works, editingWork]);
    } else {
      setWorks(works.map(work => 
        work.id === editingWork.id ? editingWork : work
      ));
    }
    
    setIsModalOpen(false);
    setEditingWork(null);
    setIsAddingNew(false);
  };

  const handleDelete = (id: number) => {
    if (confirm('確定要刪除這個作品嗎？')) {
      setWorks(works.filter(work => work.id !== id));
    }
  };

  const handleInputChange = (field: keyof StudentWork, value: string) => {
    if (!editingWork) return;
    setEditingWork({ ...editingWork, [field]: value });
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-pink-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-pink-500" style={{fontFamily: '"Pacifico", serif'}}>
                共學趣線上國語文寫作教室
              </h1>
              <span className="ml-4 text-gray-500">作品管理</span>
            </div>
            <div className="flex items-center gap-4">
              <a href="/results" className="text-gray-700 hover:text-pink-500 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer">
                返回作品展示
              </a>
              <a href="/" className="text-gray-700 hover:text-pink-500 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer">
                回到首頁
              </a>
              <button
                onClick={handleLogout}
                className="text-red-600 hover:text-red-700 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                登出
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">作品管理系統</h1>
              <p className="text-gray-600 mt-2">管理學生優秀作品，編輯作品資訊</p>
            </div>
            <button
              onClick={handleAddNew}
              className="bg-pink-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-pink-600 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
            >
              <i className="ri-add-line"></i>
              新增作品
            </button>
          </div>
        </div>
      </div>

      {/* Works List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">作品資訊</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">作者</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">年級</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">獎項</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {works.map((work) => (
                  <tr key={work.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        <img 
                          src={work.image}
                          alt={work.title}
                          className="w-16 h-12 object-cover object-top rounded"
                        />
                        <div>
                          <h3 className="font-medium text-gray-900">{work.title}</h3>
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">{work.excerpt}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{work.author}</td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-800">
                        {work.grade}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{work.award}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(work)}
                          className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded cursor-pointer"
                          title="編輯"
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                        <button
                          onClick={() => handleDelete(work.id)}
                          className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded cursor-pointer"
                          title="刪除"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {isModalOpen && editingWork && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {isAddingNew ? '新增作品' : '編輯作品'}
              </h2>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    作品標題 *
                  </label>
                  <input
                    type="text"
                    value={editingWork.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    placeholder="請輸入作品標題"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    作者姓名 *
                  </label>
                  <input
                    type="text"
                    value={editingWork.author}
                    onChange={(e) => handleInputChange('author', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    placeholder="請輸入作者姓名"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    年級 *
                  </label>
                  <select
                    value={editingWork.grade}
                    onChange={(e) => handleInputChange('grade', e.target.value)}
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  >
                    {grades.map((grade) => (
                      <option key={grade} value={grade}>{grade}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    分類 *
                  </label>
                  <select
                    value={editingWork.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  >
                    {categories.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  獲獎資訊 *
                </label>
                <input
                  type="text"
                  value={editingWork.award}
                  onChange={(e) => handleInputChange('award', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder="請輸入獲獎資訊"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  作品摘要 *
                </label>
                <textarea
                  value={editingWork.excerpt}
                  onChange={(e) => handleInputChange('excerpt', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none"
                  placeholder="請輸入作品摘要或開頭內容"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  作品圖片網址
                </label>
                <input
                  type="url"
                  value={editingWork.image}
                  onChange={(e) => handleInputChange('image', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder="請輸入圖片網址"
                />
                {editingWork.image && (
                  <div className="mt-3">
                    <img 
                      src={editingWork.image}
                      alt="預覽"
                      className="w-32 h-24 object-cover object-top rounded border"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                取消
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                {isAddingNew ? '新增' : '儲存'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
